import {useCallback, useEffect, useMemo} from 'react';
import dayjs from 'dayjs';
import {useReservation} from 'context/reservation';
import {useGuestGroup} from 'hooks/useGuestGroup';
import {getFeatureFlags} from '@chekin/core';
import {Analytics} from '@chekin/shared';
import type {BaseEventProperties, TrackedOffer} from './types';

function useAnalytics() {
  const {data: reservation} = useReservation();
  const {guestGroup} = useGuestGroup();
  const isTrackingEnabled = getFeatureFlags('POSTHOG_TRACKING');

  const buildOfferProperties = useCallback((offer: TrackedOffer) => {
    const offerPrice =
      offer.price ??
      (offer.price_type === 'SINGLE_PRICE' ? Number(offer.items?.[0]?.price) : undefined);

    return {
      offer_id: offer.id,
      offer_title: offer.title,
      offer_price: offerPrice,
      offer_category: offer.category?.toLowerCase(),
    };
  }, []);

  const baseProperties = useMemo<BaseEventProperties>(() => {
    const checkIn = reservation?.check_in_date;
    const checkOut = reservation?.check_out_date;
    const nightsOfStay =
      checkIn && checkOut ? dayjs(checkOut).diff(dayjs(checkIn), 'day') : 0;

    return {
      reservation_id: reservation?.id,
      housing_id: reservation?.housing_id,
      housing_name: reservation?.housing_name,
      nights_of_stay: nightsOfStay,
      reservation_price: reservation?.total_amount_to_pay,
      guest_group_type: guestGroup?.group_type,
      guest_group_members_count: guestGroup?.members?.length,
      known_number_of_guests: guestGroup?.known_number_of_guests,
      added_number_of_guests: guestGroup?.added_number_of_guests,
      number_of_fully_registered_guests: guestGroup?.number_of_fully_registered_guests,
      adults: guestGroup?.adults,
      children: guestGroup?.children,
      seniors: guestGroup?.seniors,
    };
  }, [reservation, guestGroup]);

  useEffect(() => {
    if (!isTrackingEnabled) {
      return;
    }
    Analytics.addEventProperties(baseProperties);
  }, [isTrackingEnabled, baseProperties]);

  const track = useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      if (!isTrackingEnabled) {
        return;
      }
      Analytics.track(eventName, {...baseProperties, ...properties});
    },
    [baseProperties, isTrackingEnabled],
  );

  const trackOffer = useCallback(
    (eventName: string, offer: TrackedOffer, properties?: Record<string, unknown>) =>
      track(eventName, {...buildOfferProperties(offer), ...properties}),
    [track, buildOfferProperties],
  );

  return {
    track,
    trackOffer,
  };
}

export {useAnalytics};
